import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { goldRateAPI, transactionAPI, paymentAPI, upiAPI } from '../services/api';
import api from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Loader2, ShoppingCart, 
  CheckCircle, AlertCircle, CreditCard, Smartphone, Check,
  Calendar, Wallet, RefreshCw, ShieldAlert, FileText
} from 'lucide-react';
import { BackButton } from '../components/common';
import { QRCodeSVG } from 'qrcode.react';

// Admin phone numbers for SIP testing access
const ADMIN_PHONES = ['9160373366'];

export const BuyPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const [amount, setAmount] = useState('');
  const [goldRate, setGoldRate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState('razorpay');
  const [hdfcPending, setHdfcPending] = useState(null); // For HDFC UPI pending state
  const [showManualConfirm, setShowManualConfirm] = useState(false); // For manual payment confirmation
  const [customOrderId, setCustomOrderId] = useState(''); // For HDFC audit - custom order ID
  
  // Check if user is the test account (9999123456)
  const isTestUser = user?.phone?.replace('+91', '').slice(-10) === '9999123456';
  
  // Check if user is admin (for SIP testing)
  const userPhone = user?.phone?.replace('+91', '').slice(-10);
  const isAdmin = user?.is_admin === true || ADMIN_PHONES.includes(userPhone);
  
  // SIP Testing State (Admin Only)
  // Note: VPA is no longer collected - users enter it on HDFC hosted page (PCI-DSS compliant)
  const [sipAmount, setSipAmount] = useState('1000');
  const [sipFrequency, setSipFrequency] = useState('monthly');
  const [sipStartDate, setSipStartDate] = useState('');
  const [sipEndDate, setSipEndDate] = useState('');
  const [sipLoading, setSipLoading] = useState(false);
  const [sipResult, setSipResult] = useState(null);
  const [sipConfig, setSipConfig] = useState(null);
  
  // PAN Verification State
  const [panInfo, setPanInfo] = useState(null);
  const [showPanBlockedModal, setShowPanBlockedModal] = useState(false);
  
  // Handle SIP redirect from HDFC hosted gateway
  useEffect(() => {
    const sipStatus = searchParams.get('sip_status');
    const orderId = searchParams.get('order_id');
    
    if (sipStatus && orderId) {
      // Clear the URL params to prevent re-triggering
      searchParams.delete('sip_status');
      searchParams.delete('order_id');
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
      
      // Fetch mandate details to get amount and frequency
      const fetchMandateDetails = async () => {
        try {
          const response = await api.get(`/sip/order-status/${orderId}`);
          const mandateData = response.data;
          
          // Get amount and frequency from mandate details
          const amount = mandateData.mandate_details?.order?.amount || 
                        mandateData.mandate_details?.mandate?.max_amount ||
                        '';
          const frequency = mandateData.mandate_details?.mandate?.frequency?.toLowerCase() || '';
          
          return { amount, frequency };
        } catch (err) {
          console.error('Failed to fetch mandate details:', err);
          return { amount: '', frequency: '' };
        }
      };
      
      // Show appropriate toast and set result based on status
      if (sipStatus === 'success') {
        toast.success('SIP mandate created successfully! Your SIP will start on the scheduled date.');
        fetchMandateDetails().then(({ amount, frequency }) => {
          setSipResult({ 
            success: true, 
            data: { order_id: orderId, status: 'active', amount, frequency } 
          });
        });
      } else if (sipStatus === 'failed') {
        toast.error('SIP mandate creation failed. Please try again.');
        setSipResult({ 
          success: false, 
          error: 'Mandate authorization failed on HDFC page' 
        });
      } else if (sipStatus === 'cancelled') {
        toast.info('SIP setup was cancelled.');
        setSipResult(null);
      } else if (sipStatus === 'pending') {
        toast.info('SIP mandate is pending approval. Check your UPI app.');
        fetchMandateDetails().then(({ amount, frequency }) => {
          setSipResult({ 
            success: true, 
            data: { order_id: orderId, status: 'pending', amount, frequency } 
          });
        });
      }
      
      // Auto-select SIP payment method to show the result
      setSelectedMethod('sip_autopay');
      
      // Refresh My SIPs list
      fetchMySips();
    }
  }, [searchParams, setSearchParams]);
  
  // My SIPs State
  const [mySips, setMySips] = useState([]);
  const [mySipsLoading, setMySipsLoading] = useState(false);
  const [cancellingMandateId, setCancellingMandateId] = useState(null);
  
  // Auto-polling refs
  const pollingIntervalRef = useRef(null);
  const pollAttemptsRef = useRef(0);
  const MAX_POLL_ATTEMPTS = 60; // 60 * 3 seconds = 3 minutes
  const SHOW_MANUAL_AFTER = 10; // Show manual option after 10 polls (30 seconds)

  const quickAmounts = [100, 500, 1000, 2000, 5000, 10000, 25000, 50000, 100000];

  const formatQuickAmount = (amt) => {
    if (amt >= 1000) {
      return `₹${amt / 1000}k`;
    }
    return `₹${amt}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rateRes, initRes] = await Promise.all([
          goldRateAPI.getRate(),
          api.get('/buy-page-init')  // Use buy-page-init which includes PAN info
        ]);
        setGoldRate(rateRes.data);
        setPaymentMethods(initRes.data.payment_methods || []);
        setSelectedMethod(initRes.data.default_payment_method || 'razorpay');
        
        // Store PAN info from buy-page-init response
        if (initRes.data.pan) {
          setPanInfo(initRes.data.pan);
          // Show blocking modal if user can't buy due to PAN
          if (!initRes.data.pan.can_buy && initRes.data.pan.required) {
            setShowPanBlockedModal(true);
          }
        }
      } catch (error) {
        toast.error('Failed to load data');
      }
    };
    fetchData();
  }, []);

  // SIP Testing: Set default dates and fetch config (Admin only)
  useEffect(() => {
    if (!isAdmin && !paymentMethods.some(m => m.id === 'sip_autopay')) return;
    
    // Set default SIP dates
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const oneYearLater = new Date(today);
    oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
    setSipStartDate(tomorrow.toISOString().split('T')[0]);
    setSipEndDate(oneYearLater.toISOString().split('T')[0]);
    
    // Fetch SIP config
    const fetchSIPConfig = async () => {
      try {
        const response = await api.get('/sip/plans');
        setSipConfig(response.data);
      } catch (error) {
        console.error('Failed to fetch SIP config:', error);
      }
    };
    fetchSIPConfig();
  }, [isAdmin, paymentMethods]);

  // SIP Create Handler - Uses HDFC Hosted Gateway (PCI-DSS compliant)
  // No VPA collection - user enters payment details on HDFC's page
  const handleCreateSIP = async (e) => {
    e.preventDefault();
    
    if (!sipAmount || parseFloat(sipAmount) < 100) {
      toast.error('Minimum SIP amount is ₹100');
      return;
    }
    
    if (!sipStartDate || !sipEndDate) {
      toast.error('Please select start and end dates');
      return;
    }
    
    setSipLoading(true);
    setSipResult(null);
    
    try {
      // Call the new hosted gateway session endpoint
      const response = await api.post('/sip/create-session', {
        amount: parseFloat(sipAmount),
        frequency: sipFrequency,
        start_date: sipStartDate,
        end_date: sipEndDate,
      });
      
      const { redirect_url, order_id } = response.data;
      
      if (redirect_url) {
        // Show success message before redirect
        toast.success('Redirecting to HDFC payment page...');
        
        // Store pending order info for return
        setSipResult({ 
          success: true, 
          data: { 
            order_id, 
            amount: sipAmount, 
            frequency: sipFrequency,
            redirect_url,
            status: 'redirecting'
          } 
        });
        
        // Redirect to HDFC hosted page after a short delay
        setTimeout(() => {
          window.location.href = redirect_url;
        }, 1500);
      } else {
        toast.error('Failed to get payment link from HDFC');
        setSipResult({ success: false, error: 'No payment link received' });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to create SIP session';
      setSipResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setSipLoading(false);
    }
  };

  // Fetch user's SIP mandates
  const fetchMySips = async () => {
    setMySipsLoading(true);
    try {
      const response = await api.get('/sip/list');
      setMySips(response.data.mandates || []);
    } catch (error) {
      console.error('Failed to fetch SIPs:', error);
    } finally {
      setMySipsLoading(false);
    }
  };

  // Cancel SIP mandate
  // Cancel SIP - handles both old mandate_id and new order_id flows
  const handleCancelSip = async (sipIdentifier) => {
    if (!window.confirm('Are you sure you want to cancel this SIP? This action cannot be undone.')) {
      return;
    }
    
    setCancellingMandateId(sipIdentifier);
    try {
      await api.delete(`/sip/${sipIdentifier}`);
      // Immediately remove from list for instant feedback
      // Check both order_id and mandate_id for backward compatibility
      setMySips(prev => prev.filter(sip => (sip.order_id || sip.mandate_id) !== sipIdentifier));
      toast.success('SIP cancelled successfully');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to cancel SIP';
      toast.error(errorMsg);
      // Refresh list on error to get accurate state
      fetchMySips();
    } finally {
      setCancellingMandateId(null);
    }
  };

  // Fetch My SIPs when SIP section is selected
  useEffect(() => {
    if (selectedMethod === 'sip_autopay') {
      fetchMySips();
    }
  }, [selectedMethod]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value);
  };

  const calculateMilli = () => {
    if (!amount || !goldRate) return 0;
    return (parseFloat(amount) / goldRate.milli_buy_rate).toFixed(2);
  };

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    const amountNum = parseFloat(amount);
    if (!amountNum || amountNum < 100) {
      toast.error('Minimum booking amount is Rs.100');
      return;
    }

    setLoading(true);
    try {
      if (selectedMethod === 'razorpay') {
        // Razorpay flow
        const sdkLoaded = await loadRazorpay();
        if (!sdkLoaded) {
          toast.error('Failed to load payment gateway');
          setLoading(false);
          return;
        }

        const response = await transactionAPI.buy(amountNum, 'razorpay');
        const { razorpay, transaction_id, milli_amount } = response.data;

        if (!razorpay?.order_id) {
          toast.error('Failed to create payment order');
          setLoading(false);
          return;
        }

        const options = {
          key: razorpay.key_id,
          amount: razorpay.amount,
          currency: razorpay.currency,
          name: razorpay.name,
          description: razorpay.description,
          order_id: razorpay.order_id,
          prefill: razorpay.prefill,
          theme: {
            color: '#C5A028',
          },
          handler: async (response) => {
            setProcessing(true);
            try {
              const verifyRes = await transactionAPI.verifyPayment({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              
              await refreshUser();
              setSuccessData({
                milli_amount: verifyRes.data.milli_amount,
                new_balance: verifyRes.data.new_balance,
                transaction_id: verifyRes.data.transaction_id
              });
              setSuccess(true);
              setProcessing(false);
              toast.success('Payment successful!');
            } catch (error) {
              console.error('Payment verification error:', error);
              setProcessing(false);
              setLoading(false);
              const errorMsg = error.response?.data?.detail || 'Payment verification failed. Please check your transaction history.';
              toast.error(errorMsg);
            }
          },
          modal: {
            ondismiss: async () => {
              try {
                await transactionAPI.abortTransaction(transaction_id);
              } catch (error) {
                console.error('Failed to abort transaction:', error);
              }
              setLoading(false);
            }
          }
        };

        const razorpayInstance = new window.Razorpay(options);
        razorpayInstance.on('payment.failed', (response) => {
          toast.error(response.error.description || 'Payment failed');
          setLoading(false);
        });
        razorpayInstance.open();
      } else if (selectedMethod === 'hdfc_upi' || selectedMethod === 'upi_gateway') {
        // UPI Gateway flow - smooth like Razorpay
        // Use milli_buy_rate for calculation (NOT rate_22kt which is per gram)
        const milliRate = goldRate?.milli_buy_rate;
        if (!milliRate || milliRate <= 0) {
          toast.error('Unable to fetch current gold rate. Please try again.');
          setLoading(false);
          return;
        }
        const milliAmount = parseFloat((amountNum / milliRate).toFixed(2));
        const response = await upiAPI.initiate(amountNum, milliAmount, milliRate);
        const data = response.data;

        if (!data.success) {
          toast.error(data.message || 'Failed to initiate UPI payment');
          setLoading(false);
          return;
        }

        // Store pending transaction info for the payment modal
        setHdfcPending({
          transaction_id: data.order_id,
          milli_amount: data.milli_amount,
          merchant_vpa: data.merchant_vpa,
          merchant_name: data.merchant_name,
          amount: data.amount,
          upi_intent_url: data.upi_intent_url,
          upi_links: data.app_links
        });
        
        setLoading(false);
        
        // Open UPI app automatically on mobile
        if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
          window.location.href = data.upi_intent_url;
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  // Check UPI payment status - silent version for auto-polling
  const checkHdfcStatusSilent = useCallback(async (transactionId) => {
    if (!transactionId) return;
    
    try {
      // Use UPI Gateway status endpoint via axios
      const response = await upiAPI.checkStatus(transactionId);
      const res = response.data;
      
      if (res.status === 'completed') {
        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        await refreshUser();
        setSuccessData({
          milli_amount: res.milli_amount,
          new_balance: res.new_balance,
          transaction_id: transactionId
        });
        setSuccess(true);
        setHdfcPending(null);
        toast.success('Payment successful!');
      } else if (res.status === 'failed') {
        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        toast.error(res.message || 'Payment failed');
        setHdfcPending(null);
      }
      // For 'pending' status, do nothing - continue polling silently
    } catch (error) {
      // Silently ignore errors during polling
      console.log('UPI status check error:', error);
    }
  }, [refreshUser]);

  // Start auto-polling when HDFC pending is set
  useEffect(() => {
    if (hdfcPending?.transaction_id) {
      pollAttemptsRef.current = 0;
      setShowManualConfirm(false); // Reset manual confirm when new payment starts
      
      // Start polling every 3 seconds
      pollingIntervalRef.current = setInterval(() => {
        pollAttemptsRef.current += 1;
        
        // Show manual confirmation option after SHOW_MANUAL_AFTER attempts
        if (pollAttemptsRef.current >= SHOW_MANUAL_AFTER) {
          setShowManualConfirm(true);
        }
        
        if (pollAttemptsRef.current >= MAX_POLL_ATTEMPTS) {
          // Stop after max attempts
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          return;
        }
        
        checkHdfcStatusSilent(hdfcPending.transaction_id);
      }, 3000);
      
      // Cleanup on unmount or when hdfcPending changes
      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }
  }, [hdfcPending?.transaction_id, checkHdfcStatusSilent]);

  // Also check when page becomes visible (user returns from UPI app)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && hdfcPending?.transaction_id) {
        checkHdfcStatusSilent(hdfcPending.transaction_id);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [hdfcPending?.transaction_id, checkHdfcStatusSilent]);

  // Check HDFC payment status - manual version with loading indicator
  const checkHdfcStatus = async () => {
    if (!hdfcPending?.transaction_id) return;
    
    setProcessing(true);
    try {
      // Use the new UPI Gateway status endpoint
      const res = await upiAPI.checkStatus(hdfcPending.transaction_id);
      if (res.data.status === 'completed') {
        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        await refreshUser();
        setSuccessData({
          milli_amount: res.data.milli_amount,
          new_balance: res.data.new_balance,
          transaction_id: hdfcPending.transaction_id
        });
        setSuccess(true);
        setHdfcPending(null);
        toast.success('Payment successful!');
      } else if (res.data.status === 'failed') {
        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        toast.error(res.data.message || 'Payment failed');
        setHdfcPending(null);
      } else {
        // Show more detailed feedback for pending status
        const hdfc_status = res.data.hdfc_status;
        if (hdfc_status && hdfc_status !== 'PENDING') {
          toast.info(`Bank status: ${hdfc_status}. Please wait or try again.`);
        } else {
          toast.info('Payment is still pending. Please complete in your UPI app or wait for bank confirmation.');
        }
      }
    } catch (error) {
      console.error('Status check error:', error);
      toast.error('Failed to check payment status. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Manual payment confirmation - for when auto-detection fails
  const confirmPaymentManually = async () => {
    if (!hdfcPending?.transaction_id) return;
    
    setProcessing(true);
    try {
      // Submit a client callback indicating success
      const res = await transactionAPI.submitHdfcCallback({
        transaction_id: hdfcPending.transaction_id,
        status: 'S',  // Success
        status_desc: 'Payment confirmed by user',
        response_code: '00'  // Success code
      });
      
      if (res.data.success || res.data.status === 'completed') {
        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        await refreshUser();
        setSuccessData({
          milli_amount: res.data.milli_amount,
          new_balance: res.data.new_balance,
          transaction_id: hdfcPending.transaction_id
        });
        setSuccess(true);
        setHdfcPending(null);
        setShowManualConfirm(false);
        toast.success('Payment confirmed! Millis added to your account.');
      } else {
        toast.error(res.data.reason || 'Failed to confirm payment');
      }
    } catch (error) {
      console.error('Manual confirm error:', error);
      toast.error('Failed to confirm payment. Please contact support.');
    } finally {
      setProcessing(false);
    }
  };

  if (success && successData) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl animate-slide-up">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="font-serif text-2xl text-navy font-bold mb-2">
              Booking Successful!
            </h2>
            <p className="text-muted-foreground mb-6">
              Gold booked successfully
            </p>
            
            <div className="bg-muted/50 rounded-xl p-6 mb-6">
              <p className="text-sm text-muted-foreground mb-2">Milli's Credited</p>
              <p className="font-numbers text-4xl font-semibold text-navy">
                +{successData.milli_amount}
              </p>
              <p className="text-sm text-muted-foreground mt-4">New Balance</p>
              <p className="text-xl font-semibold text-primary font-numbers">
                {successData.new_balance} Milli's
              </p>
            </div>

            <div className="space-y-3">
              <Button
                data-testid="go-dashboard-btn"
                onClick={() => navigate('/dashboard')}
                className="w-full gold-gradient text-navy font-semibold"
              >
                Go to Dashboard
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setSuccess(false);
                  setSuccessData(null);
                  setAmount('');
                }}
                className="w-full"
              >
                Book More
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (processing) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-6" />
            <h2 className="font-serif text-xl text-navy font-semibold mb-2">
              Processing Payment...
            </h2>
            <p className="text-muted-foreground">
              Please wait while we verify your payment
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="buy-page">
      {/* Header */}
      <div className="bg-white border-b border-border px-4 py-4 sticky top-0 z-10">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <BackButton to="/dashboard" />
          <h1 className="font-serif text-xl text-navy font-semibold">Book Gold</h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 pb-32">
        {/* Rate Display */}
        <Card className="mb-6 border-gold/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Current Book Rate</p>
                <p className="font-numbers text-2xl font-semibold text-navy">
                  {formatCurrency(goldRate?.milli_buy_rate || 0)}
                  <span className="text-sm font-normal text-muted-foreground">/Milli</span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">22KT Gold</p>
                <p className="font-semibold text-primary font-numbers">
                  {formatCurrency(goldRate?.rate_22kt || 0)}/g
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* PAN Warning Banner - Show when approaching threshold */}
        {panInfo?.show_warning && !panInfo?.required && (
          <Card className="mb-6 bg-orange-50 border-orange-200" data-testid="pan-warning-banner">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <ShieldAlert className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-orange-800">PAN Submission Recommended</p>
                  <p className="text-sm text-orange-700 mt-1">
                    {panInfo.warning_message || `You have purchased ₹${(panInfo.total_buy_inr || 0).toLocaleString('en-IN')} worth of gold. Submit your PAN to avoid interruption after ₹2,00,000.`}
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-2 border-orange-300 text-orange-700 hover:bg-orange-100"
                    onClick={() => navigate('/kyc')}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Submit PAN Now
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* PAN Required Banner - Show when blocked */}
        {panInfo?.required && !panInfo?.can_buy && (
          <Card className="mb-6 bg-red-50 border-red-200" data-testid="pan-required-banner">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <ShieldAlert className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-red-800">PAN Verification Required</p>
                  <p className="text-sm text-red-700 mt-1">
                    {panInfo.block_reason || 'You have crossed ₹2,00,000 in purchases. PAN verification is required to continue buying gold.'}
                  </p>
                  <Button 
                    className="mt-2 bg-red-600 hover:bg-red-700 text-white"
                    size="sm"
                    onClick={() => navigate('/kyc')}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Submit PAN to Continue
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Amount Input */}
        <Card className="mb-6 shadow-lg border-gold/20">
          <CardHeader className="pb-3">
            <CardTitle className="font-serif text-lg text-navy">Enter Amount</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative mb-4">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-semibold text-navy font-numbers">
                ₹
              </span>
              <Input
                data-testid="amount-input"
                type="number"
                placeholder="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-12 h-16 text-3xl font-semibold text-navy text-center font-numbers"
                min="10"
              />
            </div>

            {/* Quick Amount Buttons */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              {quickAmounts.map((amt) => (
                <Button
                  key={amt}
                  variant={amount === String(amt) ? 'default' : 'outline'}
                  onClick={() => setAmount(String(amt))}
                  className={`text-sm ${amount === String(amt) ? 'gold-gradient text-navy' : ''}`}
                >
                  {formatQuickAmount(amt)}
                </Button>
              ))}
            </div>

            {/* Milli Preview */}
            {amount && parseFloat(amount) >= 100 && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 animate-fade-in">
                <div className="flex items-center justify-between">
                  <span className="text-green-700">You'll receive</span>
                  <span className="font-numbers text-2xl font-semibold text-green-700">
                    {calculateMilli()} Milli's
                  </span>
                </div>
                <p className="text-green-600/70 text-sm mt-1 font-numbers">
                  ≈ {(calculateMilli() / 1000).toFixed(4)} grams of gold
                </p>
              </div>
            )}

            {amount && parseFloat(amount) > 0 && parseFloat(amount) < 100 && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-orange-600" />
                <span className="text-orange-700 text-sm">Minimum amount is Rs.100</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Current Balance */}
        <Card className="mb-6 border-gold/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Your Current Balance</span>
              <span className="font-semibold text-navy">
                {user?.milli_balance?.toFixed(2) || '0.00'} Milli's
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Payment Method Selection */}
        {paymentMethods.length > 1 && (
          <Card className="mb-6 border-gold/20">
            <CardHeader className="pb-3">
              <CardTitle className="font-serif text-lg text-navy">Select Payment Method</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {paymentMethods.map((method) => (
                <div key={method.id}>
                  <div
                    data-testid={`payment-method-${method.id}`}
                    onClick={() => method.enabled && setSelectedMethod(method.id)}
                    className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                      !method.enabled 
                        ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                        : selectedMethod === method.id 
                          ? method.is_test_mode ? 'border-orange-400 bg-orange-50 cursor-pointer' : 'border-gold bg-gold/5 cursor-pointer'
                          : 'border-gray-200 hover:border-gold/50 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        !method.enabled ? 'bg-gray-200' :
                        method.is_test_mode ? 'bg-orange-100' :
                        selectedMethod === method.id ? 'bg-gold/20' : 'bg-gray-100'
                      }`}>
                        {method.icon === 'credit-card' ? (
                          <CreditCard className={`w-5 h-5 ${!method.enabled ? 'text-gray-400' : selectedMethod === method.id ? 'text-gold' : 'text-gray-500'}`} />
                        ) : method.icon === 'calendar' ? (
                          <Calendar className={`w-5 h-5 ${!method.enabled ? 'text-gray-400' : method.is_test_mode ? 'text-orange-600' : selectedMethod === method.id ? 'text-gold' : 'text-gray-500'}`} />
                        ) : (
                          <Smartphone className={`w-5 h-5 ${!method.enabled ? 'text-gray-400' : method.is_test_mode ? 'text-orange-600' : selectedMethod === method.id ? 'text-gold' : 'text-gray-500'}`} />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={`font-semibold ${method.enabled ? 'text-navy' : 'text-gray-500'}`}>{method.name}</span>
                          {method.badge && (
                            <Badge 
                              variant={method.badge === 'Live' ? 'default' : 'secondary'} 
                              className={`text-xs ${
                                method.badge === 'Coming Soon' ? 'bg-gray-200 text-gray-600' : 
                                method.badge === 'UAT' || method.badge === 'Test Mode' ? 'bg-orange-500 text-white' :
                                method.badge === 'New' ? 'bg-green-500 text-white' : ''
                              }`}
                            >
                              {method.badge}
                            </Badge>
                          )}
                        </div>
                        <p className={`text-xs ${method.is_test_mode ? 'text-orange-600 font-medium' : 'text-muted-foreground'}`}>{method.description}</p>
                      </div>
                    </div>
                    {method.enabled && selectedMethod === method.id && (
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${method.is_test_mode ? 'bg-orange-500' : 'bg-gold'}`}>
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  {/* Test Mode Warning */}
                  {method.is_test_mode && selectedMethod === method.id && (
                    <div className="mt-2 p-3 bg-orange-100 border border-orange-300 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-orange-800">⚠️ TEST MODE</p>
                          <p className="text-xs text-orange-700 mt-1">{method.test_warning}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Custom Order ID for HDFC Audit - Only for test user */}
                  {isTestUser && method.id === 'hdfc_upi' && selectedMethod === 'hdfc_upi' && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="w-full">
                          <p className="text-sm font-semibold text-blue-800">🔧 HDFC Audit Mode</p>
                          <p className="text-xs text-blue-700 mt-1 mb-2">Enter a custom Order ID (max 35 chars) for HDFC security audit. Leave blank to auto-generate.</p>
                          <Input
                            data-testid="custom-order-id-input"
                            type="text"
                            placeholder="e.g., 100000766003"
                            value={customOrderId}
                            onChange={(e) => setCustomOrderId(e.target.value)}
                            maxLength={35}
                            className="text-sm font-mono bg-white"
                          />
                          {customOrderId && (
                            <p className="text-xs text-blue-600 mt-1">Order ID: {customOrderId} ({customOrderId.length}/35 chars)</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* SIP Form - Shows when sip_autopay is selected */}
        {selectedMethod === 'sip_autopay' && (
          <Card className="mb-6 border-gold/20 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="sip-form-section">
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-lg text-navy flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gold" />
                Setup Gold SIP
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Automatic monthly gold investment via UPI AutoPay
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-2">
              {/* PAN Blocked Warning for SIP */}
              {panInfo?.required && !panInfo?.can_buy && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <ShieldAlert className="w-6 h-6 text-red-600 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-red-800">PAN Verification Required</p>
                      <p className="text-sm text-red-700 mt-1">
                        You cannot create a new SIP until your PAN is verified. 
                        You have crossed the ₹2,00,000 purchase threshold.
                      </p>
                      <Button 
                        size="sm"
                        className="mt-2 bg-red-600 hover:bg-red-700 text-white"
                        onClick={() => navigate('/kyc')}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Submit PAN Now
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* SIP Config Status */}
              {sipConfig && (
                <div className={`flex items-center gap-2 p-2 rounded-lg mb-4 ${
                  sipConfig.config?.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {sipConfig.config?.enabled ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <AlertCircle className="w-4 h-4" />
                  )}
                  <span className="text-sm font-medium">
                    {sipConfig.config?.enabled ? 'Gold SIP Available' : 'SIP Currently Unavailable'}
                  </span>
                </div>
              )}
              
              {/* Hosted Gateway Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <Smartphone className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Secure HDFC Payment Page</p>
                    <p className="text-xs text-blue-700 mt-1">
                      You will be redirected to HDFC&apos;s secure page to set up your UPI AutoPay. 
                      No payment details are collected here.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Simplified SIP Form - No VPA/debit day (handled by HDFC) */}
              <form onSubmit={handleCreateSIP} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  {/* Amount */}
                  <div className="space-y-1">
                    <Label htmlFor="sipAmount" className="text-sm font-medium">SIP Amount (₹)</Label>
                    <Input
                      id="sipAmount"
                      data-testid="sip-amount-input"
                      type="number"
                      min="100"
                      max="100000"
                      value={sipAmount}
                      onChange={(e) => setSipAmount(e.target.value)}
                      required
                      className="h-10"
                      placeholder="1000"
                    />
                    <p className="text-xs text-gray-500">Min: ₹100, Max: ₹1,00,000</p>
                  </div>

                  {/* Frequency */}
                  <div className="space-y-1">
                    <Label htmlFor="sipFrequency" className="text-sm font-medium">Frequency</Label>
                    <Select value={sipFrequency} onValueChange={setSipFrequency}>
                      <SelectTrigger className="h-10" data-testid="sip-frequency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Start Date */}
                  <div className="space-y-1">
                    <Label htmlFor="sipStartDate" className="text-sm font-medium">Start Date</Label>
                    <Input
                      id="sipStartDate"
                      data-testid="sip-start-date-input"
                      type="date"
                      min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                      value={sipStartDate}
                      onChange={(e) => setSipStartDate(e.target.value)}
                      required
                      className="h-10"
                    />
                  </div>

                  {/* End Date */}
                  <div className="space-y-1">
                    <Label htmlFor="sipEndDate" className="text-sm font-medium">End Date</Label>
                    <Input
                      id="sipEndDate"
                      data-testid="sip-end-date-input"
                      type="date"
                      min={sipStartDate || new Date(Date.now() + 86400000 * 2).toISOString().split('T')[0]}
                      value={sipEndDate}
                      onChange={(e) => setSipEndDate(e.target.value)}
                      required
                      className="h-10"
                    />
                  </div>
                </div>

                {/* SIP Summary */}
                {sipAmount && parseFloat(sipAmount) >= 100 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-green-800 mb-1">Your Gold SIP Plan</p>
                    <p className="text-xs text-green-700">
                      ₹{sipAmount} will be auto-debited {sipFrequency} starting from {sipStartDate ? new Date(sipStartDate).toLocaleDateString('en-IN') : 'selected date'}
                    </p>
                  </div>
                )}

                <Button 
                  type="submit" 
                  disabled={sipLoading || !sipConfig?.config?.enabled || (panInfo?.required && !panInfo?.can_buy)} 
                  className="w-full gold-gradient text-navy font-semibold h-12"
                  data-testid="create-sip-btn"
                >
                  {sipLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Creating SIP Session...
                    </>
                  ) : panInfo?.required && !panInfo?.can_buy ? (
                    <>
                      <ShieldAlert className="w-4 h-4 mr-2" />
                      PAN Required to Create SIP
                    </>
                  ) : (
                    <>
                      <Wallet className="w-4 h-4 mr-2" />
                      Start Gold SIP - ₹{sipAmount}/{sipFrequency === 'monthly' ? 'month' : sipFrequency === 'weekly' ? 'week' : sipFrequency === 'daily' ? 'day' : 'quarter'}
                    </>
                  )}
                </Button>
                
                <p className="text-xs text-center text-gray-500">
                  You will be redirected to HDFC&apos;s page to enter your UPI ID and approve the mandate
                </p>
              </form>

              {/* SIP Result - Updated for hosted gateway flow */}
              {sipResult && (
                <div className={`mt-4 p-4 rounded-lg ${
                  sipResult.success ? 'bg-green-100 border border-green-300' : 'bg-red-100 border border-red-300'
                }`}>
                  {sipResult.success ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-green-800">
                        {sipResult.data.status === 'redirecting' ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span className="font-medium">Redirecting to HDFC Payment Page...</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-5 h-5" />
                            <span className="font-medium">SIP Session Created!</span>
                          </>
                        )}
                      </div>
                      
                      <div className="bg-white p-3 rounded-lg border border-green-200 space-y-2">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">Order ID:</span>
                          <span className="font-mono font-medium text-navy">{sipResult.data.order_id}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">Amount:</span>
                          <span className="font-medium text-navy">₹{sipResult.data.amount}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">Frequency:</span>
                          <span className="font-medium text-navy capitalize">{sipResult.data.frequency}</span>
                        </div>
                      </div>
                      
                      {sipResult.data.status !== 'redirecting' && sipResult.data.redirect_url && (
                        <Button 
                          onClick={() => window.location.href = sipResult.data.redirect_url}
                          className="w-full bg-green-600 hover:bg-green-700 text-white"
                        >
                          Continue to HDFC Payment Page
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-red-800">
                      <AlertCircle className="w-5 h-5" />
                      <span>{sipResult.error}</span>
                    </div>
                  )}
                </div>
              )}

              {/* My Active SIPs */}
              <div className="mt-6 pt-4 border-t border-gold/20">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-navy">My SIPs</h4>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={fetchMySips}
                    disabled={mySipsLoading}
                    className="text-gold hover:text-gold/80"
                  >
                    <RefreshCw className={`w-4 h-4 ${mySipsLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
                
                {mySipsLoading ? (
                  <div className="text-center py-4 text-gray-500">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                    Loading SIPs...
                  </div>
                ) : mySips.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No active SIPs yet. Create your first Gold SIP above!</p>
                ) : (
                  <div className="space-y-3">
                    {mySips.map((sip) => (
                      <div 
                        key={sip.order_id || sip.mandate_id} 
                        className={`p-3 rounded-lg border ${
                          sip.status === 'active' ? 'bg-green-50 border-green-200' :
                          sip.status === 'pending' ? 'bg-yellow-50 border-yellow-200' :
                          sip.status === 'paused' ? 'bg-gray-50 border-gray-200' :
                          'bg-red-50 border-red-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-navy">₹{sip.amount}</span>
                              <span className="text-xs text-gray-500">/ {sip.frequency}</span>
                              <Badge className={`text-xs ${
                                sip.status === 'active' ? 'bg-green-500' :
                                sip.status === 'pending' ? 'bg-yellow-500' :
                                sip.status === 'paused' ? 'bg-gray-500' :
                                'bg-red-500'
                              } text-white`}>
                                {sip.status}
                              </Badge>
                              {sip.flow_type === 'hosted_gateway' && (
                                <Badge className="text-xs bg-blue-100 text-blue-700">HDFC</Badge>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              ID: {(sip.order_id || sip.mandate_id || '').substring(0, 12)}...
                              {/* Only show VPA for old API flow records */}
                              {sip.vpa && sip.flow_type !== 'hosted_gateway' && ` • VPA: ${sip.vpa}`}
                            </p>
                            {sip.next_execution && sip.status === 'active' && (
                              <p className="text-xs text-green-600 mt-1">
                                Next debit: {new Date(sip.next_execution).toLocaleDateString('en-IN')}
                              </p>
                            )}
                            {sip.status === 'pending' && (
                              <p className="text-xs text-yellow-600 mt-1">
                                Awaiting approval in your UPI app
                              </p>
                            )}
                          </div>
                          {(sip.status === 'active' || sip.status === 'pending' || sip.status === 'paused') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCancelSip(sip.order_id || sip.mandate_id)}
                              disabled={cancellingMandateId === (sip.order_id || sip.mandate_id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              {cancellingMandateId === (sip.order_id || sip.mandate_id) ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <span className="text-xs">Cancel</span>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* HDFC UPI Payment Modal - Clean UI similar to Razorpay */}
        {hdfcPending && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md bg-white shadow-2xl">
              <CardContent className="p-6">
                {/* Header */}
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Smartphone className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">HDFC UPI Payment</h3>
                  <p className="text-gray-500 text-sm mt-1">Complete payment in your UPI app</p>
                </div>
                
                {/* Amount Display */}
                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Amount to Pay</p>
                    <p className="text-3xl font-bold text-gray-900">₹{hdfcPending.amount}</p>
                    <p className="text-sm text-green-600 mt-1">You'll receive {hdfcPending.milli_amount} Milli's</p>
                  </div>
                  <div className="border-t mt-4 pt-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Pay to:</span>
                      <span className="font-mono font-medium">{hdfcPending.merchant_vpa}</span>
                    </div>
                  </div>
                </div>

                {/* QR Code for Desktop Users */}
                {!/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) && (
                  <div className="mb-6">
                    <p className="text-center text-sm text-gray-500 mb-3">Scan QR code with any UPI app</p>
                    <div className="flex justify-center">
                      <div className="p-4 bg-white border-2 border-gray-200 rounded-xl shadow-sm">
                        {hdfcPending.upi_intent_url ? (
                          <QRCodeSVG 
                            value={hdfcPending.upi_intent_url}
                            size={192}
                            level="L"
                            includeMargin={true}
                          />
                        ) : (
                          <div className="w-48 h-48 flex items-center justify-center text-gray-400 text-sm">
                            QR code unavailable
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* UPI App Buttons (shown on mobile or as fallback on desktop) */}
                {/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) && (
                  <div className="mb-6">
                    <p className="text-center text-sm text-gray-500 mb-3">Open your UPI app to pay</p>
                    <div className="grid grid-cols-3 gap-3">
                      <a 
                        href={hdfcPending.upi_intent_url}
                        className="flex flex-col items-center p-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                      >
                        <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mb-2">
                          <Smartphone className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-xs font-medium">Any UPI</span>
                      </a>
                      <a 
                        href={hdfcPending.upi_links?.gpay}
                        className="flex flex-col items-center p-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                      >
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-green-500 rounded-lg flex items-center justify-center mb-2">
                          <span className="text-white font-bold text-xs">G</span>
                        </div>
                        <span className="text-xs font-medium">GPay</span>
                      </a>
                      <a 
                        href={hdfcPending.upi_links?.phonepe}
                        className="flex flex-col items-center p-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                      >
                        <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg flex items-center justify-center mb-2">
                          <span className="text-white font-bold text-xs">P</span>
                        </div>
                        <span className="text-xs font-medium">PhonePe</span>
                      </a>
                    </div>
                  </div>
                )}
                
                {/* Waiting Indicator - Auto-checking */}
                <div className={`rounded-xl p-4 mb-4 ${showManualConfirm ? 'bg-orange-50 border border-orange-200' : 'bg-amber-50 border border-amber-200'}`}>
                  <div className="flex items-center gap-3">
                    <Loader2 className={`w-5 h-5 animate-spin ${showManualConfirm ? 'text-orange-600' : 'text-amber-600'}`} />
                    <div>
                      <p className={`text-sm font-medium ${showManualConfirm ? 'text-orange-900' : 'text-amber-900'}`}>
                        {showManualConfirm ? 'Still waiting for confirmation...' : 'Auto-detecting payment...'}
                      </p>
                      <p className={`text-xs ${showManualConfirm ? 'text-orange-700' : 'text-amber-700'}`}>
                        {showManualConfirm 
                          ? 'If you have completed the payment, tap "I have paid" below.' 
                          : 'Complete payment in your UPI app. We\'ll automatically confirm once done.'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="space-y-3">
                  {/* Manual Verify Button - Shows after waiting */}
                  {showManualConfirm && (
                    <Button
                      data-testid="manual-verify-btn"
                      onClick={checkHdfcStatus}
                      disabled={processing}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                    >
                      {processing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          I have paid - Verify Now
                        </>
                      )}
                    </Button>
                  )}
                  
                  <Button
                    variant="ghost"
                    onClick={() => {
                      // Stop polling when canceling
                      if (pollingIntervalRef.current) {
                        clearInterval(pollingIntervalRef.current);
                        pollingIntervalRef.current = null;
                      }
                      setHdfcPending(null);
                      setShowManualConfirm(false);
                    }}
                    className="w-full text-gray-500 hover:text-gray-700"
                  >
                    Cancel Payment
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Fixed Bottom Button - Hide when SIP is selected */}
      {selectedMethod !== 'sip_autopay' && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border p-4">
          <div className="max-w-lg mx-auto">
            <Button
              data-testid="proceed-payment-btn"
              onClick={handlePayment}
              disabled={loading || !amount || parseFloat(amount) < 100 || hdfcPending || (panInfo?.required && !panInfo?.can_buy)}
              className="w-full h-14 text-lg font-semibold gold-gradient text-navy btn-hover-lift"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : selectedMethod === 'hdfc_upi' ? (
                <Smartphone className="w-5 h-5 mr-2" />
              ) : (
                <CreditCard className="w-5 h-5 mr-2" />
              )}
              {loading ? 'Processing...' : `Pay ${amount ? formatCurrency(amount) : 'Rs.0'}`}
            </Button>
          </div>
        </div>
      )}

      {/* PAN Blocked Modal */}
      <Dialog open={showPanBlockedModal} onOpenChange={setShowPanBlockedModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <ShieldAlert className="w-6 h-6" />
              PAN Verification Required
            </DialogTitle>
            <DialogDescription className="text-left pt-2">
              <p className="mb-4">
                You have purchased <strong>₹{(panInfo?.total_buy_inr || 0).toLocaleString('en-IN')}</strong> worth of gold, 
                exceeding the ₹2,00,000 threshold.
              </p>
              <p className="mb-4">
                As per RBI guidelines, PAN verification is mandatory for gold purchases exceeding ₹2,00,000. 
                Please submit your PAN to continue buying gold.
              </p>
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="font-medium text-gray-700 mb-1">What you need:</p>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Your 10-character PAN number</li>
                  <li>A clear photo of your PAN card</li>
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-4">
            <Button 
              onClick={() => {
                setShowPanBlockedModal(false);
                navigate('/kyc');
              }}
              className="w-full gold-gradient text-navy font-semibold"
            >
              <FileText className="w-4 h-4 mr-2" />
              Submit PAN Now
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowPanBlockedModal(false)}
            >
              I'll do it later
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BuyPage;
